import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Switch, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskEmailService from '../../services/TaskEmailService';
import { taskErrorMessage } from '../../services/TaskTrackingService';
import { getAuthHeaders } from '../../utils/authUtils';
import { PageHeader } from '../common/PageHeader';
import { EmailFrame } from './EmailsTab';

const GROUPS = ['My tasks', 'Tasks I manage or watch', 'Posting'];

/**
 * /me/email-settings: which task emails the signed-in person gets. Linked from the user menu and
 * from the footer of every task email, so it's usually opened on a phone: one column.
 */
const MyEmailSettingsPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
    useTrackUsage('MyEmailSettingsPage', 'screen', 'tasks');
    const [emails, setEmails] = useState(null);
    const [error, setError] = useState(null);
    const [rowState, setRowState] = useState({});
    const [example, setExample] = useState(null);
    const [exampleMail, setExampleMail] = useState(null);

    useEffect(() => {
        TaskEmailService.mine()
            .then(setEmails)
            .catch((e) => setError(taskErrorMessage(e)));
    }, []);

    const showExample = (row) => {
        setExample(row);
        setExampleMail(null);
        TaskEmailService.myExample(row.key)
            .then(setExampleMail)
            .catch((e) => setExampleMail({ error: taskErrorMessage(e) }));
    };

    const flip = async (row, on) => {
        // Show the change at once; put it back if the save fails
        setEmails((list) => list.map((e) => (e.key === row.key ? { ...e, on } : e)));
        setRowState((s) => ({ ...s, [row.key]: null }));
        try {
            setEmails(await TaskEmailService.updateMine({ [row.key]: on }));
            setRowState((s) => ({ ...s, [row.key]: 'Saved' }));
        } catch (e) {
            setEmails((list) => list.map((x) => (x.key === row.key ? { ...x, on: !on } : x)));
            setRowState((s) => ({ ...s, [row.key]: "Couldn't save. Try again." }));
        }
    };

    return (
        <>
            <PageHeader darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />
            <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, md: 3 } }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    My email settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose which task emails you get at {getAuthHeaders().email || 'your address'}. Changes save straight
                    away.
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
                {!emails && !error && <CircularProgress />}
                {emails &&
                    GROUPS.filter((g) => emails.some((e) => e.group === g)).map((group) => (
                        <Paper key={group} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="overline" color="text.secondary">
                                {group}
                            </Typography>
                            {emails
                                .filter((e) => e.group === group)
                                .map((e) => (
                                    <Box key={e.key} sx={{ display: 'flex', gap: 1.5, py: 1, alignItems: 'flex-start' }}>
                                        <Box sx={{ width: 72, flexShrink: 0, pt: e.alwaysOn ? 0.5 : 0 }}>
                                            {e.alwaysOn ? (
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Always on
                                                </Typography>
                                            ) : (
                                                <Switch
                                                    checked={e.on}
                                                    disabled={e.adminOff}
                                                    onChange={(ev) => flip(e, ev.target.checked)}
                                                    inputProps={{ 'aria-label': e.label }}
                                                />
                                            )}
                                        </Box>
                                        <Box sx={{ minWidth: 0, pt: e.alwaysOn ? 0.5 : 1 }}>
                                            <Typography variant="body1" color={e.adminOff ? 'text.disabled' : 'text.primary'}>
                                                {e.label}
                                            </Typography>
                                            {e.alwaysOn && !e.adminOff && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Can't be turned off, so you always know when work is yours.
                                                </Typography>
                                            )}
                                            {e.adminOff && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Turned off for everyone by an admin. Your own choice is kept for when
                                                    it's back on.
                                                </Typography>
                                            )}
                                            <Button size="small" sx={{ px: 0, minWidth: 0 }} onClick={() => showExample(e)}>
                                                See example
                                            </Button>
                                            <Typography
                                                variant="caption"
                                                role="status"
                                                aria-live="polite"
                                                color={rowState[e.key] === 'Saved' ? 'text.secondary' : 'error'}
                                                sx={{ display: 'block' }}
                                            >
                                                {rowState[e.key]}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                        </Paper>
                    ))}
            </Box>
            <Dialog open={!!example} onClose={() => setExample(null)} maxWidth="md" fullWidth>
                <DialogTitle>{example?.label}: example</DialogTitle>
                <DialogContent>
                    {!exampleMail && <CircularProgress size={24} />}
                    {exampleMail?.error && <Alert severity="error">{exampleMail.error}</Alert>}
                    {exampleMail?.html && (
                        <>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <b>Subject:</b> {exampleMail.subject}
                            </Typography>
                            <EmailFrame html={exampleMail.html} />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExample(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MyEmailSettingsPage;
